import React, { useRef, useEffect, useCallback } from "react";
import MenuList from "./MenuList";
import { Mode } from "../../views/tutorV2/types";
import { useGroupMenuListView } from "../../views/tutorV2/hooks/presentation/useGroupMenuListView";
import { useSocratesV2GroupMenuListView } from "../../views/socratesV2/hooks/useSocratesV2GroupMenuListView";
import { useUIStore, useTutorChatStore } from "../../views/tutorV2/store";
import { useMediaMd } from "../../hooks/useMedia";

/**
 * GroupMenuList
 *
 * 그룹 목록(튜터 목록/소크라테스 목록) 컨테이너 컴포넌트
 *
 * 책임:
 * - 모드에 따라 적절한 훅 호출 (튜터: useGroupMenuListView, 소크라테스 V2: useSocratesV2GroupMenuListView)
 * - Intersection Observer로 무한 스크롤 구현
 * - MenuList 프레젠테이션 컴포넌트에 props 전달
 */

export interface GroupMenuListProps {
  mode: Mode;
}

const GroupMenuList: React.FC<GroupMenuListProps> = ({ mode }) => {
  const observerTarget = useRef<HTMLDivElement>(null);

  // 모드에 따라 적절한 훅 사용
  // Socrates 모드: V2 전용 훅 (새 API 사용)
  // Tutor 모드: 기존 훅 (레거시 API 사용)
  const tutorView = useGroupMenuListView({ mode });
  const socratesV2View = useSocratesV2GroupMenuListView();

  // 모드에 따라 적절한 뷰 선택
  const currentView = mode === Mode.SOCRATES ? socratesV2View : tutorView;

  const {
    items,
    label,
    editingItemId,
    isLoading,
    hasMore,
    isFetchingMore,
    handleItemClick,
    handleItemEdit,
    handleItemDelete,
    handleItemSave,
    handleItemTitleChange,
    handleLoadMore,
  } = currentView;

  const { openGroup, setOpenGroup, closeNav } = useUIStore();
  const { fetching } = useTutorChatStore();
  const isMd = useMediaMd();

  // Intersection Observer로 무한 스크롤 구현
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore && !isFetchingMore) {
        handleLoadMore();
      }
    },
    [hasMore, isFetchingMore, handleLoadMore]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, { threshold: 0 });
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [handleObserver]);

  return (
    <MenuList
      label={label}
      icon={mode === Mode.SOCRATES ? "ailine" : "chatline"}
      items={items}
      open={openGroup}
      setOpen={setOpenGroup}
      editingItemId={editingItemId}
      isLoading={isLoading}
      onItemClick={(itemId) => {
        handleItemClick(itemId);
        if (!isMd) closeNav();
      }}
      onItemEdit={handleItemEdit}
      onItemDelete={handleItemDelete}
      onItemSave={handleItemSave}
      onItemTitleChange={handleItemTitleChange}
      observerTarget={observerTarget}
      hasMore={hasMore}
      disabled={fetching}
    />
  );
};

export default GroupMenuList;
